import { Injectable } from '@angular/core';
import { HttpService } from '../_shared/services/http.service';
import { environment } from '../../environments/environment';
import { ConfigitQuote, QuotationQuote, QuoteService } from '../_shared/services/quote.service';
import {
    PriceSummaryHelperService,
    ProductWithGroup,
} from '../_shared/components/price-summary/price-summary.helper.service';
import {
    OfferCreationResponse,
    OfferParams,
    Prices,
    PriceSummary,
    SetQuoteAsClosedParams,
} from '../_shared/components/price-summary/price-summary.model';
import { concatMap, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { ProductList } from '../_shared/components/product-list/product-list.service';
import { isGroupWithHintText } from '../_shared/utils/offer.util';
import moment from 'moment/moment';

@Injectable({
    providedIn: 'root',
})
export class SummaryDataService {
    constructor(
        private httpService: HttpService,
        private helperService: PriceSummaryHelperService,
        private translate: TranslateService,
        private quoteService: QuoteService
    ) {}

    public closeQuote(
        customerId: string,
        quotationRequestId: string,
        voucherCode: string,
        productList: ProductList,
        lastPriceSummary: PriceSummary
    ): Observable<{ salesDocumentNumber: string }> {
        const products: ProductWithGroup[] = productList.groups.flatMap((group) =>
            isGroupWithHintText(group)
                ? [
                      {
                          shortText: group.name,
                          printOptions: group.printOptions,
                          material: '',
                          selected: true,
                          quantity: 0,
                          group: undefined,
                      },
                  ]
                : group.products.map((product) => ({
                      ...product,
                      group: this.translate.instant(group.translationKey),
                  }))
        );
        const quote = this.quoteService.getCurrentQuote();
        const payload = this.helperService.getOfferCreationParams(
            customerId,
            voucherCode,
            products,
            productList.sampleOfferId,
            quote
        );
        payload.salesforceRequestId = quotationRequestId;
        if (this.quoteService.hasConfigitModel('Photovoltaics')) {
            const today = moment.utc().startOf('day');
            payload.expirationDate = today.add(4, 'weeks').format('YYYY-MM-DD');
        }
        return quote.isLegacy
            ? this.closeConfigitQuote(payload, quote.configitQuote, <Prices>lastPriceSummary.prices)
            : this.closeQuotationQuote(payload, quote.quotationQuote);
    }

    public getAvailablePrintTypes(salesOrg: string) {
        const url = `${environment.http.quotation}offers/print/types?salesOrg=${salesOrg}`;
        return this.httpService.get(url);
    }

    private closeQuotationQuote(payload: OfferParams, quote: QuotationQuote) {
        const url = `${environment.http.quotation}quotations/quote-assist/quotes/${quote.id}/close`;
        return this.httpService
            .post<QuotationQuote>(url, payload)
            .pipe(map((response) => ({ salesDocumentNumber: response.offerNumber })));
    }

    private setConfigitQuoteAsClosed({ documentId, revision }: ConfigitQuote, payload: SetQuoteAsClosedParams) {
        const url = `${environment.http.baseUrl}quote/${documentId}/revision/${revision}/close`;
        return this.httpService.patch<{ salesDocumentNumber: string }>(url, payload);
    }

    private closeConfigitQuote(payload: OfferParams, quote: ConfigitQuote, prices: Prices) {
        return this.httpService.post<OfferCreationResponse>(`${environment.http.quotation}offers`, payload).pipe(
            concatMap(({ documentNumber, expirationDate }) =>
                this.setConfigitQuoteAsClosed(quote, {
                    sapDocumentNumber: documentNumber,
                    expirationDate,
                    customerId: payload.customerNumber,
                    prices,
                    voucherCode: payload.voucherId,
                })
            )
        );
    }
}
